<script lang="ts">
  import { onMount, SvelteComponent } from "svelte";
  import { writable, Writable, Readable, readable, get } from "svelte/store";

  import Window from "../components/Window.svelte";
  import { Dir, FileData, filesInDirectory } from "../scripts/FileTypes";
  import { FileMgr } from "../stores/FileMgr";
  import { addLog } from "../stores/LoggerStore";
  import Commands from "../scripts/CommandParser";
  import Path from "../scripts/Path";
  import type { WinProps, WinCbs, Theme } from "../scripts/WinTypes";
  import ProcMgr, { resetSaved, WinProc } from "../stores/ProcMgr";
  import { clamp, focusout, randomId } from "../scripts/utils";
  import axios from "axios";

  import PromptTextView from "../components/PromptTextView.svelte";
  import PromptTableView, {
    TableData,
  } from "../components/PromptTableView.svelte";

  import PromptGridView, {
    PromptFileViewGroup,
  } from "../components/PromptGridView.svelte";
  import SettingsMgr from "../stores/SettingsMgr";

  //Bindings============================================================
  let inputElem: HTMLTextAreaElement,
    contElem: HTMLElement,
    inputValue = "",
    inputFocus;

  //Theme ============================================================
  const _theme = SettingsMgr.getInstance().theme;
  const themeWatcher = (theme: Theme) => {
    if (!contElem) {
      return;
    }
    const c = theme.colors;
    contElem.style.setProperty("color", c["1"]);
    contElem.style.setProperty("background-color", c["2"]);
    return theme.colors;
  };
  $: _colors = themeWatcher($_theme);
  //============================================================
  interface ViewMap {
    [key: string]: typeof SvelteComponent;
  }
  const viewMap: ViewMap = {
    PromptTextView: PromptTextView,
    PromptTableView: PromptTableView,
    PromptGridView: PromptGridView,
  };
  //global
  export let winProps: WinProps;
  const handleFocus = () => {
    if (inputElem) {
      // console.log("handle focus");
      inputElem.focus();
    }
    return true;
  };

  let closeWindow, maximizeWindow;
  const setCloseWindow = (func) => {
    closeWindow = func;
    return true;
  };
  const setMaximizeWindow = (func) => {
    maximizeWindow = func;
    return true;
  };
  const callbacks: WinCbs = {
    onFocus: handleFocus,
    closeWindow: setCloseWindow,
    maximizeWindow: setMaximizeWindow,
  };
  let inputArea;
  winProps.name = winProps.name || "Terminal";
  const procmgr = ProcMgr.getInstance();
  const filemgr = FileMgr.getInstance();
  let fileMgrDir = filemgr.dirs;
  const watchFileMgrDir = (_dirs) => {
    while (!_dirs.some((dir) => dir.node.path.isSame(pwd))) {
      addError(`No directory : ${pwd.path}. Moving back to ${pwd.parent}...`);
      pwd.set(new Path(pwd.parent));
    }
  };
  $: _fileMgrDirWatcher = watchFileMgrDir($fileMgrDir);
  const commands = new Commands();
  let cmdHistory: Writable<string[]> = writable([]);
  class _CmdUpdater {
    public update(str: string) {
      cmdHistory.update((cmds) => [...cmds, str]);
      return this;
    }
    public get length() {
      return get(cmdHistory).length;
    }
    public at(index: number) {
      return get(cmdHistory).at(index);
    }
    public set(hist) {
      cmdHistory.set(hist);
      cmdHistoryCursor = hist.length;
    }
  }
  const CmdUpdater = new _CmdUpdater();
  let cmdHistoryCursor = 0;
  const setNewCmdHistory = (cmd: string) => {
    CmdUpdater.update(cmd);
    cmdHistoryCursor = CmdUpdater.length;
  };

  //up arrow
  const _cmdHistoryCursorMover = () => {
    cmdHistoryCursor = clamp(cmdHistoryCursor, 0, CmdUpdater.length);
    return CmdUpdater.at(cmdHistoryCursor) || ""; //return empty when out of range
  };
  const cmdHistoryUp = () => {
    cmdHistoryCursor--;
    inputValue = _cmdHistoryCursorMover();
  };
  const cmdHistoryDown = () => {
    cmdHistoryCursor++;
    inputValue = _cmdHistoryCursorMover();
  };

  let username = "jam",
    address = "127.0.0.1";
  let user = `${username}@${address}`;
  let _pwd = writable(new Path("~/"));
  class _PwdUpdater {
    public set(path: Path) {
      _pwd.set(path);
      return this.p;
    }
    private get p() {
      return get(_pwd);
    }
    public get path() {
      return this.p.path;
    }
    public get parent() {
      return this.p.parent;
    }
  }
  const pwd = new _PwdUpdater();
  $: cmdUser = `${user} ${$_pwd.path}/ > `;

  let itemId = 1;
  interface PromptItem {
    id?: string;
    comp: string;
    data: {};
  }
  let promptItems: Writable<PromptItem[]> = writable([]);

  const lineElem = () => {
    return {
      comp: "PromptTextView",
      id: "" + itemId++,
      data: {
        isLine: true,
      },
    };
  };
  const _add = (item: PromptItem, addLine = true) => {
    item.id = "" + itemId++; //randomId();
    if (addLine) {
      promptItems.update((promptItems) => [item, lineElem(), ...promptItems]);
      return;
    }
    promptItems.update((promptItems) => [item, ...promptItems]);
  };
  const addPs = () => {
    const data = get(procmgr.procStore).map((proc) => {
      // 0. this?
      // 1. id
      // 2. name
      // 3. type = windowed || deamon
      // 4. window z index
      // 5. if based on file, file
      const retval = [
        proc.id === winProps.id ? ">" : "",
        proc.id,
        proc.props.name,
        proc.isWindow ? "windowed" : "daemon",
        "",
        "",
      ];
      if (!proc.isWindow) {
        return retval;
      }
      const p = proc as WinProc;
      retval[4] = "" + p.props.zIndex;
      if (p.props.node) {
        retval[5] = p.props.node.path.path;
      }
      return retval;
    });
    const psData: TableData = {
      desc: "- Process List",
      firstColumnColor: _colors["okay"],
      heads: ["This", "ID", "Name", "Type", "Z-Index", "File path"],
      rows: data,
    };
    const processItem = <PromptItem>{
      comp: "PromptTableView",
      data: { tableData: psData },
    };
    _add(processItem, false);
  };
  const addHelp = () => {
    const items = [];
    const titleAndApps: TableData = {
      title: "JamOS Terminal Commands",
      desc: `- Applications, [optional] <required>`,
      heads: ["App Name", "Description"],
      rows: [
        ["about", "About JamOS🍞"],
        ["bakery", "Let's begin from here"],
        ["terminal", "CLI interface that can control JamOS"],
        ["finder [$path]", "displays and interacts with directories and files"],
        ["notepad [$path]", "easy and simple text editor"],
        ["markdown [$path]", "markdown editor and viewer"],
        ["browser [$url]", "JamBrowser"],
        ["broom", "close all process"],
        ["settings", "open user settings"],
        ["styler", "JamOS Styler"],
        ["atelier", "Interactive canvas with generative art"],
      ],
    };
    const terminalCmds: TableData = {
      desc: "- Terminal Commands",
      heads: ["Command Name", "Description"],
      rows: [
        ["ls [$path]", "list all directories and files"],
        ["cd <$path>", "goes to the path"],
        ["mkdir <$path>", "makes directories recursively to the path"],
        [
          "touch <$path>",
          "makes a file in text mode and creates directories to the file",
        ],
        ["cat <$path>", "reads a text file"],
        ["rm <$path>", "removes a file"],
        ["rmdir <$path>", "removes a directory recursively"],
        ["clear", "clears prompt"],
        ["get <$url>", "sends get request to url"],
        ["whoami", "shows your username"],
        ["maximize", "toggle window maximize"],
        ["quit, exit", "quits this terminal"],
        ["ps", "process list"],
      ],
    };

    const systemCmds: TableData = {
      desc: `- System commands, or dev/beta apps`,
      heads: ["App Name", "Description"],
      rows: [
        ["savebread", "save current status"],
        ["loadbread", "load saved"],
        ["resetbread", "reset all data and settings and reboot"],
        ["get <$url>", "<DEV> send get request and display response"],
        ["postman", "<DEV> send request and receive response"],
        [
          "logger",
          "<DEV>A logger watches over the whole OS and displays changes",
        ],
        [
          "hub",
          "<To be updated> where you can create and share your bread with your friends.",
        ],
        ["mv <$path:from> <$path:to>", "<To be updated>moves a file"],
        ["cp <$path:from> <$path:to>", "<To be updated>copies a file"],
      ],
    };

    titleAndApps.rows.sort();
    terminalCmds.rows.sort();

    const appItem = <PromptItem>{
      comp: "PromptTableView",
      data: { tableData: titleAndApps },
    };
    const terminalItem = <PromptItem>{
      comp: "PromptTableView",
      data: { tableData: terminalCmds },
    };
    const systemCmdItem = <PromptItem>{
      comp: "PromptTableView",
      data: { tableData: systemCmds },
    };
    addText(" End of help", true);
    _add(systemCmdItem, false);
    addText(" ", false);
    _add(terminalItem, false);
    addText(" ", false);
    _add(appItem, false);
  };

  const addWarn = (prom: string, line: boolean = true) => {
    addText(prom, line, "warn");
  };
  const addError = (prom: string, line: boolean = true) => {
    addText(prom, line, "error");
  };

  const addSuccess = (prom: string, line: boolean = true) => {
    addText(prom, line, "okay");
  };

  const addText = (prom: string, line: boolean = true, type?: string) => {
    const data = {
      text: prom,
    };
    if (type) {
      data["colorType"] = type;
    }
    const textItem = <PromptItem>{
      comp: "PromptTextView",
      data: data,
    };
    _add(textItem, line);
  };

  const clearPrompt = () => {
    promptItems.set([]);
  };

  const handleCommand = (e) => {
    const toBeParsed: string = inputValue.trim();
    clearCommand(e);

    const words = toBeParsed
      .split(" ")
      .map((word) => word.trim())
      .filter((word) => word !== " " && word !== "");

    if (!words.length) {
      //no input commands
      addText(" ");
      return;
    }
    setNewCmdHistory(toBeParsed);

    // const cmd = words.at(0);
    // if (!commands.validateCommand(cmd)) {
    //   addError(`Invalid command : ${cmd}`);
    //   return;
    // }

    handleTerminalCmd(words);
    // addText("Command : " + words.join(" "));
    addText(words.join(" "));
  };

  const handleTerminalCmd = async (cmds: string[]) => {
    const cmd = cmds.at(0);
    // if (!commands.TerminalCommandList.includes(cmd)) {
    //   addLog(`No terminal command : ${cmd} from handleTerminalCmd`);
    //   return;
    // }
    let dest: string;
    let merged = cmds.slice(1).join(" ").trim();
    let mergedFilePath = merged.startsWith("~/")
      ? new Path(merged)
      : Path.join(pwd.path, merged);
    let _pwd;

    if (cmd.startsWith("./")) {
      const app = Path.join(pwd.path, cmd.slice(2));
      if (filemgr.findFile(app)) {
        procmgr.exeFile(app);
        addText("Execute " + app.last);
        return;
      }
    }

    switch (cmd) {
      case "atelier":
      case "postman":
      case "terminal":
      case "logger":
      case "markdown":
      case "bakery":
      case "broom":
      case "about":
      case "styler":
      case "settings":
        procmgr.add(cmd);
        break;
      case "notepad":
        if (merged.length === 0) {
          procmgr.add("notepad");
        } else {
          console.log(merged);
          procmgr.add("notepad", { path: mergedFilePath.path });
        }
        break;
      case "viewer":
        if (merged.length === 0) {
          addWarn("Path required for image viewer.");
          break;
        }

        procmgr.add("viewer", { path: mergedFilePath.path });
        break;
      case "cp":
      case "mv":
        addWarn("To be updated soon");
        break;
      case "_test":
        const s = JSON.stringify(get(promptItems));
        const p = JSON.parse(s);
        console.log("s:", s);
        console.log("p:", p);
        break;
      case "get":
        dest = merged;
        if (!dest || dest.length === 0) {
          addWarn("Path required for get request");
          return;
        }
        addText("Get request to " + dest);
        const res = await axios
          .get(dest)
          .then((response) => response.data)
          .catch((err) => {
            console.log(err);
            addError(err);
            return;
          });

        addText("Fetched data : " + JSON.stringify(res));
        break;

      case "mkdir":
        if (!merged) {
          addWarn("Path required");
          return;
        }
        if (!filemgr.mkdir(mergedFilePath)) {
          addError(`Failed to make directory at ${merged}`);
          return;
        }
        addSuccess(`Successfully made directory at ${merged}`);
        break;
      case "touch":
        if (!merged) {
          addWarn("Path required");
          return;
        }
        if (!filemgr.touch(mergedFilePath.path, "text")) {
          addError(`Failed to make file at ${merged}`);
          return;
        }
        addSuccess(`Successfully made file at ${merged}`);
        break;

      case "rm":
        if (!merged) {
          addWarn("Path required");
          return;
        }
        const rmf = filemgr.findFile(mergedFilePath);
        if (!rmf) {
          addError("No file! " + mergedFilePath.path);
          if (filemgr.findDirValue(mergedFilePath)) {
            addWarn("If you want to remove a directory, use 'rmdir'.");
          }
          return;
        }
        if (filemgr.rm(mergedFilePath)) {
          addText("File removed");
        } else {
          addWarn("Failed to remove file");
        }
        return;
      case "savebread":
      case "loadbread":
      case "resetbread":
        let forceAction = false;
        if (merged === "-f") {
          forceAction = true;
        }
        procmgr.exeCmd(cmd, { force: forceAction });
        return;
      case "rmdir":
        if (!merged) {
          addWarn("Path required");
          return;
        }
        if (!filemgr.findDirValue(mergedFilePath)) {
          addError("No directory! " + mergedFilePath.path);
          return;
        }
        const info = filesInDirectory(filemgr.findDirValue(mergedFilePath));
        focusout();
        procmgr.openModal({
          title: "Remove directory",
          descs: [
            `Directory ${mergedFilePath.path} and its files will be removed,`,
            `which contains ${info.totalDirs} directories and ${info.totalFiles} files.`,
          ],
          buttons: ["Remove", "Cancel"],
          callbacks: [
            () => {
              // procmgr.modalResult = { result: "remove" };
              if (filemgr.rmdir(mergedFilePath)) {
                addText("Directory removed");
              } else {
                addWarn(
                  `Failed to remove directory. ${
                    mergedFilePath.isHome ? 'Directory was "home".' : ""
                  }`
                );
              }
              procmgr.closeModal();
              {
                console.log("InputELem focus");
                inputElem.focus();
              }
            },
            () => {
              // procmgr.modalResult = { result: "cancel" };
              addWarn("Canceled.");
              procmgr.closeModal();
              console.log("InputELem focus");
              inputElem.focus();
            },
          ],
        });

        return;
      case "ls":
        //join path if exists
        const dir = filemgr.lsValue(mergedFilePath);
        if (!dir) {
          addError(`No directory : ${mergedFilePath.path}`);
          break;
        }
        const buildPromptGridGroups = (dir: Dir): PromptItem => {
          const item: PromptItem = {
            comp: "PromptGridView",
            data: { pwd: dir.node.path, groups: <PromptFileViewGroup[]>[] },
          };

          const groups: PromptFileViewGroup[] = [];
          const _dirs: PromptFileViewGroup = {
            type: "Directory",
            // color: "#27e8a7",
            color: _colors["1"],
            items: [],
          };
          const _apps: PromptFileViewGroup = {
            type: "Application",
            // color: "#add7ff",
            color: _colors["1"],
            items: [],
          };
          const _texts: PromptFileViewGroup = {
            type: "Text file",
            // color: "#dbdbdb",
            color: _colors["1"],
            items: [],
          };
          dir.dirs.forEach((_) => {
            _dirs.items.push(_.node.path.last + "/");
          });
          dir.files.forEach((_) => {
            if (_.node.type === "text") {
              _texts.items.push(_.node.path.last);
              return;
            }
            _apps.items.push("./" + _.node.path.last);
          });
          const pushIfExists = (arr) => {
            if (arr.items.length) {
              item.data["groups"].push(arr);
            }
          };
          pushIfExists(_dirs);
          pushIfExists(_apps);
          pushIfExists(_texts);

          return item;
        };

        _add(buildPromptGridGroups(dir));
        break;
      case "ps":
        addPs();

        break;
      case "cd":
        if (!merged) {
          addWarn("Path required");
          return;
        }
        if (!filemgr.findDirValue(mergedFilePath)) {
          addError(`Directory '${mergedFilePath.path}' does not exist `);
          return;
        }
        pwd.set(mergedFilePath);
        break;
      case "cat":
        dest = cmds.slice(1).join(" ");
        if (!dest) {
          addWarn("cat : path required");
          return;
        }
        const textpath = Path.join(pwd.path, dest);
        const f = filemgr.findFile(textpath);
        if (!f) {
          addError(`File : '${f.node.path.path}' does not exist `);
          return;
        }
        if (f.node.type !== "text") {
          addError(`${textpath.path} is not a text file!`);
          return;
        }
        addText((f.data as any)?.text);
        break;
      case "finder":
        dest = Path.join(pwd.path, cmds.at(1)).path;
        procmgr.add("finder", { path: dest });
        break;
      case "browser":
        dest = cmds.at(1) || "";
        procmgr.add("browser", { path: dest });
        break;
      case "whoami":
        addText(username);
        break;

      //Terminal commands
      case "pwd":
        addText(`${pwd.path}`);
        break;
      case "clear":
        clearPrompt();
        break;
      case "help":
        addHelp();
        break;
      case "exit":
      case "quit":
        closeWindow();
        break;
      case "maximize":
        maximizeWindow();
        break;
      default:
        addError("No command : " + cmds.join(" "));
        break;
    }
  };

  const autoComplete = (e) => {
    e.preventDefault();

    //path auto complete
    {
      const detachPwd = (path: string) => {
        if (!path.startsWith(pwd.path)) {
          //if input path does not begin with pwd, then return input value untouched
          return path;
        }
        return new Path(path.slice(pwd.path.length)).path;
      };

      const vals = inputValue
        .split(" ")
        .filter((str) => str !== " " && str !== "");
      if (!vals.at(0)) {
        return;
      }
      const cmd = vals.at(0);
      const cmdRequiresPath = [
        "cd",
        "ls",
        "touch",
        "rm",
        "rmdir",
        "mv",
        "mkdir",
        "cat",
        "finder",
        "notepad",
        "markdown",
        "viewer",
      ];
      if (cmd.startsWith("./") && cmd.length >= 3 && vals.length === 1) {
        const input = cmd.slice(2);
        const pathToInput = Path.join(pwd.path, input);
        const dir = filemgr.findDirValue(new Path(pathToInput.parent));
        if (!dir) {
          addWarn("Cannot execute : " + cmd);
          return;
        }
        const startWiths = dir.files.filter((file) =>
          file.node.path.startsWith(pathToInput)
        );
        if (startWiths.length === 0) {
          addWarn("No executable : " + cmd);
          return;
        }
        inputValue = `./${detachPwd(startWiths.at(0).node.path.path)}`;
        return;
      }
      if (cmdRequiresPath.includes(cmd) && vals.length === 1) {
        addWarn(`Add path to interact : ${vals.at(0)} path/to/a/file`);
        return;
      }
      if (cmdRequiresPath.includes(cmd) && vals.length >= 2) {
        let p = Path.join(pwd.path, vals.at(1));
        if (vals.at(1) && vals.at(1).startsWith("~/")) {
          p = new Path(vals.at(1));
        }
        addLog("path : " + p.path + " , parent : " + p.parent);
        const dir = filemgr.lsValue(new Path(p.parent));
        if (dir) {
          //auto complete
          const startsWithsDir = [];
          const startsWithsFiles = [];
          dir.dirs
            .filter((node) => node.node.path.path.startsWith(p.path))
            .forEach((dir) => startsWithsDir.push(dir.node.path));
          dir.files
            .filter((node) => node.node.path.path.startsWith(p.path))
            .forEach((file) => startsWithsFiles.push(file.node.path));

          const dirCount = startsWithsDir.length,
            fileCount = startsWithsFiles.length;
          if (dirCount + fileCount <= 0) {
            //no match
            return;
          }

          if (dirCount + fileCount === 1) {
            if (dirCount === 1) {
              inputValue = `${vals.at(0)} ${detachPwd(
                startsWithsDir.at(0).path
              )}/`;
              return;
            }
            if (fileCount === 1) {
              inputValue = `${vals.at(0)} ${detachPwd(
                startsWithsFiles.at(0).path
              )}`;
              return;
            }
          }
          // return directory of shortest first if multiple matches, else file shortest path
          if (dirCount + fileCount > 1) {
            if (dirCount > 0) {
              inputValue = `${vals.at(0)} ${detachPwd(
                startsWithsDir.at(0).path
              )}/`;
              return;
            }
            inputValue = `${vals.at(0)} ${detachPwd(
              startsWithsFiles.at(0).path
            )}`;
            return;
          }
        }
      }
    }

    const cmds = commands.autoCompleteCommand(inputValue);
    // addLog(cmds);
    if (cmds.length === 0) {
      addWarn(`No app begins with : "${inputValue}"`);
      return;
    }
    if (cmds.length === 1) {
      addLog("set auto complete");
      inputValue = cmds.at(0);
      return;
    }
    addText("Possible commands : " + cmds.join(", "));
    addLog(inputValue);
  };

  const clearCommand = (e) => {
    e.preventDefault();
    inputValue = "";
    cmdHistoryCursor = CmdUpdater.length;
  };

  const handleKeydown = (e) => {
    {
      // addLog(e.key);
      // addLog(`Cursor ${cmdHistoryCursor}, last cmd : ${cmdHistory.at(0)}`);
    }
    const keyMap = {
      Enter: handleCommand,
      Tab: autoComplete,
      Escape: clearCommand,
      ArrowDown: cmdHistoryDown,
      ArrowUp: cmdHistoryUp,
    };
    keyMap[e.key]?.(e);
  };
  onMount(async () => {
    _colors = themeWatcher($_theme);
    clearPrompt();

    const terminal = winProps["terminal"];
    if (terminal) {
      if (terminal["welcome"]) {
        const _actions = [
          () => {
            addHelp();
          },
          () => {
            addText("Hello world from JamOS!");
          },
          () => {
            addText("You can control the whole OS with this terminal.");
          },
          () => {
            addText("If you are first at JamOS, try out 'Bakery'.");
          },
          () => {
            addText("Have fun being here and good luck!");
          },
          () => {
            inputValue = "You can now type whatever you want.";
          },
          () => {
            inputValue = "";
            inputElem?.focus();
          },
        ];
        _actions.forEach((func, idx) => {
          setTimeout(func, 1000 + idx * 1500);
        });
      }
    }
    const welcome = () => {
      const welcomeMsg = `Type "help" to get started`;
      addText(welcomeMsg);
    };
    if (winProps.data?.cmdPwd) {
      pwd.set(winProps.data?.cmdPwd);
    }
    if (winProps.data?.cmdHistory) {
      CmdUpdater.set(winProps.data?.cmdHistory);
    }
    if (winProps.data?.promptItems) {
      // console.log("restored : ", winProps.data.promptItems);
      promptItems.set(winProps.data.promptItems);
      itemId = get(promptItems).length + 1;
    } else {
      welcome();
    }
  });

  /// data transfer with winproc----------------------

  procmgr.setWinDataFecther(winProps.id, "promptItems", () => promptItems);
  procmgr.setWinDataFecther(winProps.id, "cmdHistory", () => cmdHistory);
  procmgr.setWinDataFecther(winProps.id, "cmdPwd", () => _pwd);
</script>

<Window {winProps} {callbacks}>
  <div class="container" on:mouseup={handleFocus} bind:this={contElem}>
    <div class="input-area" bind:this={inputArea}>
      <span class="input-label ver-center">{cmdUser}</span>
      <span class="input-box-wrapper">
        <textarea
          spellcheck="false"
          rows="1"
          class="input-box ver-center"
          bind:this={inputElem}
          bind:value={inputValue}
          on:keydown={handleKeydown}
        />
      </span>
      <!-- <span class="input-focus" bind:this={inputFocus} /> -->
    </div>
    <div class="seperator" />

    {#each $promptItems as item (item.id)}
      <!-- <div class="prompt-result">> {prompt}</div> -->
      <svelte:component this={viewMap[item.comp]} {...item.data} />
    {/each}
  </div>
  <div class="css-keeper input-box" contenteditable="true"><br /></div>
</Window>

<style>
  .container {
    position: relative;
    width: 100%;
    min-height: 100%;
  }

  .input-area {
    width: 100%;
    display: flex;
    height: 20px;
    padding: 10px 0;
    overflow: hidden;
  }

  .input-label {
    /* width: 200px; */
    padding-left: 10px;
  }
  .input-box-wrapper {
    flex: 1;
    overflow: hidden;
    background-color: transparent;
    color: inherit;
    padding-right: 10px;
  }
  .input-box {
    border: none;
    outline: none;
    width: 100%;
    height: 100%;
    color: inherit;
    resize: none;
    background-color: transparent;

    /* keep in single line */
    white-space: nowrap;
    overflow: hidden;
  }
  .input-box::selection {
    color: #333;
    background: #dbdbdb;
  }

  .seperator {
    width: 100%;
    border-bottom: 1px solid #dbdbdb;
  }

  .prompt-result {
    width: 100%;
    display: block;
  }

  .css-keeper {
    display: none;
  }
</style>
